import React, { ReactElement, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useAtomValue } from 'jotai';
import { AccountInfoAtom, LoginStatusAtom } from '../../store/AccountStore';
import { isEmail } from '../../utils/utils';
import { Link } from '../../Devon/Components/InkyLink';
import KlaviyoManager from '../../Components/Klaviyo/KlaviyoManager';
import KodanshaEmail from '../KodanshaUIKit/KodanshaEmail/KodanshaEmail';
import KodanshaSelect from '../KodanshaUIKit/KodanshaSelect/KodanshaSelect';
import KodanshaCheckBox from '../KodanshaUIKit/KodanshaCheckBox/KodanshaCheckBox';
import KodanshaArrow from '../KodanshaUIKit/KodanshaArrow/KodanshaArrow';
import './ContactUsStrip.scss';

export default function ContactUsStrip(): ReactElement {
    const loginStatus = useAtomValue(LoginStatusAtom);
    const AccountInfo = useAtomValue(AccountInfoAtom);
    const isLoggedIn = loginStatus === 'LoggedIn';

    const [supportSent, setSupportSent] = useState(false);
    const [error, setError] = useState('');
    const [supportReason, setSupportReason] = useState('General Inquiry');
    const [acceptPrivacyStatement, setAcceptPrivacyStatement] = useState(false);
    const [customerSupport, setCustomerSupport] = useState('');
    const [supportEmail, setSupportEmail] = useState(AccountInfo.email ?? '');

    const [extraFieldsDict, setExtraFieldsDict] = useState<Record<string, string>>({});


    type Reason = {
        label: string;
        order: number;
    } & (
        {
            disabled: false;
            extraFields?: ExtraField[];
        } | {
        disabled: true;
        disabledMessage: React.ReactNode;
    }
        )
    type ExtraField = {
        label: string;
    }

    const Mailto = ({ email, subject, body, children }: {
        email: string;
        subject?: string;
        body?: string;
        children: React.ReactNode;
    }) => {
        let href = `mailto:${email}`;
        const params = [];
        if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
        if (body) params.push(`body=${encodeURIComponent(body)}`);
        if (params.length > 0) {
            href += `?${params.join('&')}`;
        }

        return (
            <a style={{
                textDecoration: 'underline',
                cursor: 'pointer',
            }} href={href}>{children}</a>
        );
    };

    const unsortedReasons:Reason[] = [
        {
            label: 'General inquiry',
            disabled: false,
            order: 1,
        },
        {
            label: 'Orders',
            disabled: false,
            order: 2,
        },
        {
            label: 'Managing your account',
            disabled: false,
            order: 3,
        },
        {
            label: 'Devices & digital solutions',
            disabled: false,
            order: 4,
        },
        {
            label: 'Submissions',
            disabled: true,
            order: 8,
            disabledMessage: `We currently focus on translating and publishing titles that are originally released in Japan. While we're not publishing original works for now, please know that we support and value your creative efforts. We wish you the best of luck and encourage you to continue your great work!`,
        },
        {
            label: 'Suggestions & feedback',
            disabled: false,
order:12
        }, {
            label: 'Publishing suggestions',
            disabled: true,
            order: 6,
            disabledMessage: <>Let us know which series you’d like Kodansha to publish next! Whether you’re a fan of a
                particular manga, light novel, or book that you believe deserves a place in our catalog, your
                suggestions are important to us. Please take a moment to complete this <Link target='_blank'
                                                                                             to={{ pathname: 'https://forms.gle/fTVapKxpuoADLKjVA' }}
                                                                                             rel='noopener noreferrer'
                                                                                             style={{
                                                                                                 color: '#000',
                                                                                                 textDecoration: 'underline',
                                                                                                 cursor: 'pointer',
                                                                                             }}
                >Publishing suggestion form</Link> to share your thoughts with us.</>,
        },
        {
            label: 'Rights inquiries',
            disabled: true,
            order: 10,
            disabledMessage:<>For a more appropriate response to your request, we invite you to complete the <Link target='_blank'
                                                                                                                   to={{ pathname: 'https://rights-form.kodansha.co.jp/VideoGoods?type=1&language=en' }}
                                                                                                                   rel='noopener noreferrer'
                                                                                                                   style={{
                                                                                                                       color: '#000',
                                                                                                                       textDecoration: 'underline',
                                                                                                                       cursor: 'pointer',
                                                                                                                   }}
            >Right inquiries form.</Link>
            </>
        },
        {
            label: 'Suspected piracy & copyright infringement',
            disabled: false,
            order: 11,
        },
        {
            label: 'Print issues',
            disabled: true,
            disabledMessage: <>To help us address the issue with your volume(s), we need the following information:
                <div style={{
                    marginLeft: '20px',
                }}>
                    <ul>
                        <li>Series name and volume number</li>
                        <li>Picture(s) of the issue</li>
                        <li>Picture of the copyright page of the affected volume(s)</li>
                        <li>Your full name, mailing address and phone number</li>
                    </ul>
                </div>
                Please send an email to <Mailto email={'hello@kodanshausa.com'} subject={"Support - Print Issue"} body={` 
                Series name and volume number:
                
                Picture(s) of the issue:
                
                Picture of the copyright page of the affected volume(s):
                
                Your full name, mailing address and phone number:
                
                `}>hello@kodanshausa.com</Mailto> with all
                this information and a description of the issue.
            </>,
order:5
        },
        {
            label: 'Donation request',
            disabled: false,
            order: 9,
            extraFields: [
                {
                    label: 'Full name'
                },
                {
                    label: 'Name of the establishment'
                },
                {
                    label: 'City'
                },
                {
                    label: 'Address'
                }
            ]
        },
        {
            label: 'Reprint recommendations',
            disabled: true,
            order: 7,
            disabledMessage: <>{"Do you have any specific volumes in mind that you think Kodansha USA should consider reprinting? We'd like to receive more feedback from our readers. Please take a moment to complete this "}<Link target='_blank'
                           to={{ pathname: 'https://forms.gle/H8zqtrtg9sjmb3hZ7' }}
                           rel='noopener noreferrer'
                           style={{
                               color: '#000',
                               textDecoration: 'underline',
                               cursor: 'pointer',
                           }}>Reprint recommendation form</Link> to share your thoughts with us.</>,
        },
        {
            label: 'Retailers',
            disabled: false,
            order: 12,
        },
    ];
    const reasons = unsortedReasons.sort((a, b) => a.order - b.order);
    const getReasonObj =
        reasons.find(r => r.label === supportReason) ?? reasons[0];

    async function sendInquiry(): Promise<void> {
        if (!isEmail(supportEmail)) {
            setError('Please enter a valid email.');
            return;
        }
        if (customerSupport === '') {
            setError('Please fill the description.');
            return;
        }
        if(getReasonObj.disabled){
            setError('This option is disabled');
            return;
        }

        if (getReasonObj.disabled === false && getReasonObj.extraFields !== undefined
            && getReasonObj.extraFields?.length > 0) {
            const extraFields = getReasonObj.extraFields;

            const missingFields = [];
            for (const extraField of extraFields) {
                const value = extraFieldsDict[extraField.label];

                if (!value || value.length < 1) {
                    missingFields.push(extraField.label);
                }
            }

            if (missingFields.length > 0) {
                setError('Please fill the required fields:' + missingFields.join(', '));
                return;
            }


            const combinedMessageWithFields = `${extraFields.map((key) => `${key.label}: ${extraFieldsDict[key.label]}`).join('<br>')}` + '<br>Message:' + '<br><br>' + customerSupport;

            await KlaviyoManager.sendCustomerRequest(combinedMessageWithFields, supportEmail, supportReason, isLoggedIn);
        } else {
            await KlaviyoManager.sendCustomerRequest(customerSupport, supportEmail, supportReason, isLoggedIn);
        }

        setSupportSent(true);
    }

    return (
        <div className='contact-us-strip-wrapper' id='contact-us-support'>
            <div className='contact-us-hours'>
                <div className='contact-us-hours-information-wrapper'>
                    <span className='contact-us-hours-information-second-title'>{'WE\'RE HERE TO HELP'}</span>
                    <h3 className='contact-us-hours-information-main-title'>Contact Us</h3>
                    <div className='contact-us-hours-information-divider' />
                    <span className='contact-us-hours-our-best'>
                       We review inquiries regularly during business days and will do our best to get back to you as soon as possible.
                    </span>
                </div>
                <div className='contact-us-hours-contact-form-wrapper'>
                    <div className='contact-us-hours-contact-form-placeholder'>
                        Contact Form
                        {!supportSent && (
                            <div className='contact-us-hours-contact-form-group-wrapper'>
                                {!isLoggedIn ? (
                                    <div>
                                        <div className='contact-us-form-group-label'>Email</div>
                                        <KodanshaEmail
                                            placeHolder='Email'
                                            name='Email'
                                            type='Email'
                                            label='Email'
                                            onChange={(email): void => {
                                                setSupportEmail(email);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className='uses-email'>
                                        We will send an email to your registered account
                                        at <span>{AccountInfo.email}</span>
                                    </div>
                                )}
                                <Form.Group className='supportFormGroup'>
                                    <Form.Label>Select your topic</Form.Label>
                                    <KodanshaSelect
                                        title={reasons[0].label}
                                        disableTextTransform={true}
                                        onChange={(obj): void => {
                                            setSupportReason(obj);
                                        }}
                                        items={reasons.map((reason) => reason.label)}
                                    />
                                </Form.Group>
                                {/*Don't simplify below line or the type guards get confused*/}
                                {getReasonObj.disabled===false && <>
                                    {getReasonObj.extraFields?.map((extraField) => (
                                        <div key={extraField.label}>
                                            <div className='contact-us-form-group-label'>{extraField.label}</div>
                                            <input
                                                className='supportFormGroup-input-one-line'
                                                name={extraField.label}
                                                defaultValue={extraFieldsDict[extraField.label]??''}
                                                required={true}
                                                onChange={(s): void => {
                                                    setExtraFieldsDict(prev => ({
                                                        ...prev,
                                                        [extraField.label]: s.target.value,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    ))}
                                    <div className='contact-us-form-group-label'>Your message</div>
                                    <textarea
                                        className='supportFormGroup-input'
                                        name='Contact Form'
                                        onChange={(s): void => {
                                            setCustomerSupport(s.target.value);
                                        }}
                                    />
                                    <span className='support-center-error'>{error}</span>
                                    <KodanshaCheckBox
                                        onChange={(): void => {
                                            setAcceptPrivacyStatement(acceptPrivacyStatement => !acceptPrivacyStatement);
                                        }}
                                        checked={acceptPrivacyStatement}
                                        label={(
                                            <div>
                                                I accept the <Link target='_blank' to='/privacy-policy'
                                                                   style={{
                                                                       color: '#000',
                                                                       textDecoration: 'underline',
                                                                       cursor: 'pointer',
                                                                   }}
                                            >privacy policy</Link>
                                            </div>
                                        )}
                                    />
                                </>
                                }
                                {getReasonObj?.disabled && (<>
                                    <div className='contact-us-form-group-disabled-message'>
                                        {getReasonObj.disabledMessage}
                                    </div>
                                </>)
                                }
                                <div className='wrapper-message-btn-hide-on-small'
                                     style={
                                         {
                                             display:(!getReasonObj?.disabled)?undefined:"none"
                                         }
                                }
                                >
                                    <KodanshaArrow
                                        text='send message to customer support'
                                        styles='contact-us-hours-contact-form-button'
                                        active={!getReasonObj?.disabled && acceptPrivacyStatement}
                                        onClick={sendInquiry}
                                    />
                                </div>
                                <div className='wrapper-message-btn-hide-on-large'
                                     style={
                                    {
                                         display:(!getReasonObj?.disabled)?undefined:"none"
                                    }
                                }>
                                    <span className='message-text'>Send message to customer support</span>
                                    <KodanshaArrow
                                        text='Send'
                                        styles='contact-us-hours-contact-form-button'
                                        active={!getReasonObj?.disabled && acceptPrivacyStatement}
                                        onClick={sendInquiry}
                                    />
                                </div>
                            </div>
                        )}
                        {supportSent && <div className='success-inquiry-sent'>Your inquiry has been sent</div>}
                    </div>
                </div>
            </div>
        </div>
    );
}
